import { useCallback, useEffect, useRef, useState } from "react";
import type {
  WaveLastSeenRecord,
  WaveParticipantPresence,
  WaveParticipantRecord,
  WavePresenceRecord,
} from "@vida/wave-core";
import {
  initialsFromEmail,
  initialsFromLabel,
  participantAccentColor,
  participantAccentEmail,
  participantPresenceEntries,
} from "@vida/wave-core";

type Props = {
  presences: WavePresenceRecord[];
  currentSub: string;
  /** JWT email — aligns roster «Tu» with block owner avatar color. */
  currentUserEmail?: string;
  participants?: WaveParticipantRecord[];
  lastSeen?: WaveLastSeenRecord[];
  ownerSub?: string | null;
};

const MAX_VISIBLE = 5;
const LONG_PRESS_MS = 500;

function formatLastSeen(iso: string | null): string {
  if (!iso) return "Mai connesso";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Mai connesso";
  return date.toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function resolveEmail(entry: WaveParticipantPresence): string {
  if (entry.email?.trim()) return entry.email.trim();
  if (entry.label.includes("@")) return entry.label;
  return entry.label;
}

function tooltipLines(entry: WaveParticipantPresence): string[] {
  const email = resolveEmail(entry);
  if (entry.isSelf) {
    return entry.isOnline
      ? [email, "Tu — online su questo dispositivo"]
      : [email, "Tu — offline"];
  }
  if (entry.isOnline) {
    return [email, "Online"];
  }
  const lastSeenLabel = formatLastSeen(entry.lastSeenAt);
  return [email, `Offline — ultima connessione ${lastSeenLabel}`];
}

type AvatarProps = {
  entry: WaveParticipantPresence;
  participants: WaveParticipantRecord[];
  currentUserEmail: string;
  stackIndex: number;
};

function PresenceAvatar({ entry, participants, currentUserEmail, stackIndex }: AvatarProps) {
  const [forcedOpen, setForcedOpen] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lines = tooltipLines(entry);
  const tooltipEmail = resolveEmail(entry);
  const accentHints = {
    email: entry.email,
    label: entry.isSelf ? undefined : entry.label,
    sessionEmail: entry.isSelf ? currentUserEmail : undefined,
  };
  const accentEmail = participantAccentEmail(entry.sub, participants, accentHints);
  const color = participantAccentColor(entry.sub, participants, accentHints).hex;
  const initials = accentEmail
    ? initialsFromEmail(accentEmail)
    : initialsFromLabel(entry.isSelf ? "Tu" : entry.label, tooltipEmail);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setForcedOpen(false), 2500);
  }, []);

  useEffect(() => {
    return () => {
      clearLongPressTimer();
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, [clearLongPressTimer]);

  return (
    <div
      className={`presence-avatar${entry.isSelf ? " presence-avatar--self" : ""}${forcedOpen ? " presence-avatar--tooltip-open" : ""}${entry.isOnline ? "" : " presence-avatar--offline"}`}
      style={{ zIndex: stackIndex }}
      onTouchStart={() => {
        clearLongPressTimer();
        longPressTimer.current = setTimeout(() => {
          setForcedOpen(true);
          scheduleHide();
        }, LONG_PRESS_MS);
      }}
      onTouchEnd={() => {
        clearLongPressTimer();
      }}
      onTouchCancel={() => {
        clearLongPressTimer();
      }}
      onContextMenu={(e) => e.preventDefault()}
      aria-label={`${lines[0]} — ${lines[1]}`}
    >
      <span
        className="presence-avatar-circle"
        aria-hidden="true"
        style={{ backgroundColor: color }}
      >
        {initials}
      </span>
      {entry.isSelf && (
        <span className="presence-avatar-self-badge" aria-hidden="true">
          Tu
        </span>
      )}
      {entry.isOnline && (
        <span className="presence-avatar-online-dot" aria-hidden="true" />
      )}
      {entry.isOnline && entry.deviceCount > 1 && (
        <span className="presence-avatar-device-count" aria-hidden="true">
          ×{entry.deviceCount}
        </span>
      )}
      <span className="presence-avatar-tooltip" role="tooltip">
        {lines.map((line) => (
          <span key={line} className="presence-avatar-tooltip-line">
            {line}
          </span>
        ))}
      </span>
    </div>
  );
}

export function WavePresenceOverlay({
  presences,
  currentSub,
  currentUserEmail = "",
  participants = [],
  lastSeen = [],
  ownerSub = null,
}: Props) {
  const entries = participantPresenceEntries(
    participants,
    presences,
    lastSeen,
    currentSub,
    ownerSub,
    { includeSelf: true, currentUserEmail },
  );
  if (!entries.length) return null;

  const visible = entries.slice(0, MAX_VISIBLE);
  const overflow = entries.length - MAX_VISIBLE;
  /** Left → right; self stays rightmost (near Condividi). */
  const visualOrder = [...visible].reverse();
  let stackIndex = 0;

  return (
    <div className="presence-overlay" aria-label="Partecipanti">
      {overflow > 0 && (
        <span className="presence-overlay-overflow" style={{ zIndex: stackIndex++ }}>
          +{overflow}
        </span>
      )}
      {visualOrder.map((entry) => (
        <PresenceAvatar
          key={entry.sub}
          entry={entry}
          participants={participants}
          currentUserEmail={currentUserEmail}
          stackIndex={++stackIndex}
        />
      ))}
    </div>
  );
}
